import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { Button, Icon, Menu } from 'semantic-ui-react';
import { usePopup } from '../../lib/popup';
import { OrganizationSwitcher } from '@clerk/clerk-react';

import Paths from '../../constants/Paths';
import { useIsMobile } from '../../hooks/use-media-query';
import NotificationsStep from './NotificationsStep';
import User from '../User';
import UserStep from '../UserStep';
import Bot from '../Bot';
import MobileDrawer from '../MobileDrawer/MobileDrawer';

import styles from './Header.module.scss';

const POPUP_PROPS = {
  position: 'bottom right',
};

const Header = React.memo(
  ({
    project,
    user,
    notifications,
    boards,
    currentBoardId,
    isLogouting,
    canEditProject,
    canEditUsers,
    onProjectSettingsClick,
    onUsersClick,
    onNotificationDelete,
    onUserSettingsClick,
    onSyncFromClerk,
    onLogout,
  }) => {
    const isMobile = useIsMobile();
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    const handleProjectSettingsClick = useCallback(() => {
      if (canEditProject) {
        onProjectSettingsClick();
      }
    }, [canEditProject, onProjectSettingsClick]);

    const handleSyncClick = useCallback(() => {
      if (onSyncFromClerk) {
        onSyncFromClerk();
      }
    }, [onSyncFromClerk]);

    const handleDrawerOpen = useCallback(() => setIsDrawerOpen(true), []);
    const handleDrawerClose = useCallback(() => setIsDrawerOpen(false), []);

    const NotificationsPopup = usePopup(NotificationsStep, POPUP_PROPS);
    const UserPopup = usePopup(UserStep, POPUP_PROPS);

    const switcherColor = project ? '#1A1A1A' : '#fff';
    const switcherHoverBg = project ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.1)';

    if (isMobile) {
      return (
        <>
          <div
            className={classNames(
              styles.wrapper,
              styles.wrapperMobile,
              project && styles.wrapperInProject
            )}
          >
            <button
              type="button"
              className={styles.hamburgerButton}
              onClick={handleDrawerOpen}
              aria-label="Open menu"
            >
              <Icon fitted name="sidebar" />
            </button>

            {project ? (
              <Link to={Paths.ROOT} className={styles.mobileBackLink}>
                <Icon fitted name="arrow left" size="small" />
              </Link>
            ) : (
              <Link to={Paths.ROOT} className={styles.mobileLogoLink}>
                <img src="/shaka-white.png" alt="Shakkii" className={styles.logoImageMobile} />
              </Link>
            )}

            <div className={styles.mobileTitleArea}>
              {project ? (
                <span className={styles.mobileTitleText}>{project.name}</span>
              ) : (
                <span className={styles.mobileLogoText}>Shakkii</span>
              )}
            </div>

            <NotificationsPopup items={notifications} onDelete={onNotificationDelete}>
              <button type="button" className={styles.mobileIconButton} aria-label="Notifications">
                <Icon fitted name="bell" />
                {notifications.length > 0 && (
                  <span className={styles.notificationMobile}>{notifications.length}</span>
                )}
              </button>
            </NotificationsPopup>
          </div>

          <MobileDrawer
            isOpen={isDrawerOpen}
            onClose={handleDrawerClose}
            user={user}
            project={project}
            notifications={notifications}
            boards={boards}
            currentBoardId={currentBoardId}
            isLogouting={isLogouting}
            canEditProject={canEditProject}
            onProjectSettingsClick={onProjectSettingsClick}
            onNotificationDelete={onNotificationDelete}
            onUserSettingsClick={onUserSettingsClick}
            onLogout={onLogout}
          />

          {project && <Bot />}
        </>
      );
    }

    return (
      <>
        <div className={classNames(styles.wrapper, project && styles.wrapperInProject)}>
          {!project && (
            <Link to={Paths.ROOT} className={classNames(styles.logo, styles.logoContainer)}>
              <img src="/shaka-white.png" alt="Shakkii" className={styles.logoImage} />
              <span className={styles.logoText}>Shakkii</span>
            </Link>
          )}
          <Menu inverted size="large" className={styles.menu}>
            {project && (
              <Menu.Menu position="left">
                <Menu.Item
                  as={Link}
                  to={Paths.ROOT}
                  className={classNames(styles.item, styles.itemHoverable)}
                >
                  <Icon fitted name="arrow left" />
                </Menu.Item>
                <Menu.Item className={classNames(styles.item, styles.title)}>
                  {project.name}
                  {canEditProject && (
                    <Button
                      className={classNames(styles.editButton, styles.target)}
                      onClick={handleProjectSettingsClick}
                    >
                      <Icon fitted name="setting" size="small" />
                    </Button>
                  )}
                </Menu.Item>
              </Menu.Menu>
            )}
            <Menu.Menu position="right">
              <Menu.Item className={styles.item}>
                <OrganizationSwitcher
                  hidePersonal={false}
                  appearance={{
                    elements: {
                      rootBox: {
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '34px',
                        maxHeight: '34px',
                      },
                      organizationSwitcherTrigger: {
                        color: switcherColor,
                        fontSize: '15px',
                        fontWeight: '500',
                        borderRadius: '4px',
                        padding: '6px 10px',
                        height: '34px',
                        '&:hover': {
                          backgroundColor: switcherHoverBg,
                        },
                      },
                    },
                  }}
                />
              </Menu.Item>
              <NotificationsPopup items={notifications} onDelete={onNotificationDelete}>
                <Menu.Item className={classNames(styles.item, styles.itemHoverable)}>
                  <Icon fitted name="bell" />
                  {notifications.length > 0 && (
                    <span className={styles.notification}>{notifications.length}</span>
                  )}
                </Menu.Item>
              </NotificationsPopup>
              <UserPopup
                isLogouting={isLogouting}
                onSettingsClick={onUserSettingsClick}
                onLogout={onLogout}
              >
                <Menu.Item className={classNames(styles.item, styles.itemHoverable)}>
                  <span className={styles.userName}>{user.name}</span>
                  <User name={user.name} avatarUrl={user.avatarUrl} size="small" />
                </Menu.Item>
              </UserPopup>
            </Menu.Menu>
          </Menu>
        </div>
        {project && <Bot />}
      </>
    );
  }
);

Header.propTypes = {
  /* eslint-disable react/forbid-prop-types */
  project: PropTypes.object,
  user: PropTypes.object.isRequired,
  notifications: PropTypes.array.isRequired,
  boards: PropTypes.array,
  /* eslint-enable react/forbid-prop-types */
  currentBoardId: PropTypes.string,
  isLogouting: PropTypes.bool.isRequired,
  canEditProject: PropTypes.bool.isRequired,
  canEditUsers: PropTypes.bool.isRequired,
  onProjectSettingsClick: PropTypes.func.isRequired,
  onUsersClick: PropTypes.func.isRequired,
  onNotificationDelete: PropTypes.func.isRequired,
  onUserSettingsClick: PropTypes.func.isRequired,
  onSyncFromClerk: PropTypes.func,
  onLogout: PropTypes.func.isRequired,
};

Header.defaultProps = {
  project: undefined,
  boards: undefined,
  currentBoardId: undefined,
};

export default Header;
